import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryKeys } from '@/api/query-client';
import type { paths } from '@/api/generated/schema';

/** Tous les templates d'habillage, actifs et inactifs (vue admin). */
export function useVideoOverlayTemplates() {
  return useQuery({
    queryKey: queryKeys.videoOverlayTemplates,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/video-overlay-templates');

      return data!;
    },
  });
}

type CreateVideoOverlayTemplateBody = NonNullable<
  paths['/api/v1/admin/video-overlay-templates']['post']['requestBody']
>['content']['application/json'];

/**
 * Crée un template d'habillage et dépose son logo, en deux temps comme les pièces jointes de
 * support (voir `useCreateAttachmentUploadTicket` côté frontend) : la commande renvoie une URL
 * présignée, le logo est déposé DIRECTEMENT sur le stockage objet, puis l'upload est confirmé.
 */
export function useCreateVideoOverlayTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      logo,
      ...fields
    }: Omit<CreateVideoOverlayTemplateBody, 'logoFileExtension' | 'logoContentType' | 'logoSizeBytes'> & {
      logo: File;
    }) => {
      const logoFileExtension = logo.name.includes('.') ? logo.name.slice(logo.name.lastIndexOf('.')) : '';

      const { data: ticket } = await api.POST('/api/v1/admin/video-overlay-templates', {
        body: {
          ...fields,
          logoFileExtension,
          logoContentType: logo.type,
          logoSizeBytes: logo.size,
        },
      });

      if (!ticket) {
        throw new Error("Impossible d'obtenir une URL de téléversement pour le logo.");
      }

      const uploadResponse = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: ticket.requiredHeaders,
        body: logo,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Échec du dépôt du logo (${uploadResponse.status}).`);
      }

      await api.POST(
        '/api/v1/admin/video-overlay-templates/{videoOverlayTemplateId}/confirm-logo',
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route (voir
        // le commentaire sur `useTenantLifecycleMutation` dans platform-admin.ts).
        { params: { path: { videoOverlayTemplateId: ticket.videoOverlayTemplateId } }, body: {} },
      );

      return ticket;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.videoOverlayTemplates });
    },
  });
}

function useVideoOverlayTemplateActivationMutation(action: 'activate' | 'deactivate') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (videoOverlayTemplateId: string) => {
      await api.POST(
        action === 'activate'
          ? '/api/v1/admin/video-overlay-templates/{videoOverlayTemplateId}/activate'
          : '/api/v1/admin/video-overlay-templates/{videoOverlayTemplateId}/deactivate',
        // Voir le commentaire sur `useTenantLifecycleMutation` (platform-admin.ts) : corps
        // vide nécessaire au niveau transport même si le contrat OpenAPI n'en déclare aucun.
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        { params: { path: { videoOverlayTemplateId } }, body: {} },
      );
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.videoOverlayTemplates });
    },
  });
}

export function useActivateVideoOverlayTemplate() {
  return useVideoOverlayTemplateActivationMutation('activate');
}

export function useDeactivateVideoOverlayTemplate() {
  return useVideoOverlayTemplateActivationMutation('deactivate');
}
